<template>
    <div class="col-lg-9 mx-auto overflow-x-hidden">
        <header class="text-center mb-4">
            <workflow-navigation workflowStage="Finish" />
            <h2 class="skin-page-heading">
                Your plan
            </h2>
            <i class="bi bi-check-circle-fill text-success display-2"></i>
        </header>

        <div class="container-fluid px-0 mt-0">
            <WhatsInYourDesign :retiree="retiree"></WhatsInYourDesign>

            <Plan :retiree="retiree" :finish-info="finishInfo" v-in-view="onViewed"></Plan>

            <OrigenAdvice></OrigenAdvice>

            <GenericCostOfAdvice></GenericCostOfAdvice>

            <OptionsNowYouHaveAPlan></OptionsNowYouHaveAPlan>

            <EffectOfHigherCharges :finish-info="finishInfo"></EffectOfHigherCharges>

            <ProvidersList v-if="displayProviders" :show-charges="false" :show-cost-comparison-alert="true" title="If you just want to find a low cost provider yourself"></ProvidersList>

            <AdvisorsList></AdvisorsList>

            <AnnuityProvidersList v-if="displayAnnuityProviders"></AnnuityProvidersList>

            <div v-if="schemeSupportMode" class="row">
                <div class="col">
                    <sub-section title="Next steps" :showHelp="false">
                        <FinalSalarySchemeSupport :schemeSupportMode="schemeSupportMode"></FinalSalarySchemeSupport>
                    </sub-section>
                </div>
            </div>

            <assumptions :retiree="retiree"></assumptions>

            <RiskWarnings></RiskWarnings>

            <div v-if="showDisclaimer" class="row">
                <div class="col">
                    <footer>
                        <div>
                            <p class="disclaimer">
                                <span v-html="disclaimer"></span>
                            </p>
                        </div>
                    </footer>
                </div>
            </div>

            <div v-if="displayTrustpilot" class="row mt-4">
                <div class="col">
                    <TrustPilot></TrustPilot>
                </div>
            </div>

            <div v-if="displayPoweredBy" class="row mt-4">
                <div class="col text-center">
                    <div class="powered-by-guiide">
                        <div>Powered By</div>
                        <img src="/Content/images/logo.png" class="poweredByGuiide" alt="Powered by Guiide" />
                    </div>
                </div>
            </div>
        </div>

        <div class="container-fluid">
            <page-nav :show-next="nextButtonInfo.display" back-link="test" :next-link="nextButtonInfo.link" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import WorkflowNavigation from "@/guiide/components/common/WorkflowNavigation.vue";
import OrigenAdvice from "./finish/OrigenAdvice.vue";
import Plan from "./finish/Plan.vue";
import GenericCostOfAdvice from "./finish/GenericCostOfAdvice.vue";
import OptionsNowYouHaveAPlan from "./finish/OptionsNowYouHaveAPlan.vue";
import RiskWarnings from "./finish/RiskWarnings.vue";
import AdvisorsList from "@/guiide/components/common/AdvisorsList.vue";
import AnnuityProvidersList from "./common/AnnuityProvidersList.vue";
import PageNav from "@/guiide/components/common/PageNav.vue";
import TrustPilot from "./common/TrustPilot.vue";
import { useStore } from "vuex";
import { FinishInfoResponse } from "../ts/response/finishInfoResponse";
import EffectOfHigherCharges from "./finish/EffectOfHigherCharges.vue";
import ProvidersList from "./common/ProvidersList.vue";
import WhatsInYourDesign from "./finish/WhatsInYourDesign.vue";
import Assumptions from "./finish/Assumptions.vue";
import { Retiree } from "../ts/retiree";
import { workflow } from "../ts/workflow/workflow";
import View from "../ts/view";
import Monitoring from "../ts/monitoring/monitoring";
import FinalSalarySchemeSupport from "@/guiide/components/common/DBOptions/FinalSalarySchemeSupport.vue";
import SubSection from '@/guiide/components/common/SubSection.vue';

type NextButtonInfo = { display: boolean, link: string };

const store = useStore();
const retiree = computed<Retiree | null>(() => store.state.retiree);
const finishInfo = computed<FinishInfoResponse | null>(() => store.state.finishInfo);
const nextButtonInfo = ref<NextButtonInfo>({ display: false, link: "" });

onMounted(async() => {
    const nextWorkflowStep = await workflow.getNextWorkflowStep(View.Finish);

    if (nextWorkflowStep == null) {
        nextButtonInfo.value = { display: false, link: "" };
        return;
    }

    nextButtonInfo.value = { display: true, link: nextWorkflowStep.resource };
});

const disclaimer = computed<string>(() => store.state.configuration.disclaimer);

const displayTrustpilot = computed<boolean>(() => store.state.configuration.showTrustpilot);
const displayPoweredBy = computed<boolean>(() => store.state.configuration.showPoweredBy);

const displayProviders = computed<boolean>(() => store.state.configuration.finishDisplaysProviders);
const displayAnnuityProviders = computed<boolean>(() => store.state.configuration.finishDisplaysAnnuity);

// If the footer is shown, we expect the disclaimer to be shown there - i.e. no need to display here.
const showDisclaimer = computed<boolean>(() => !(store.state.configuration?.footer?.visibleInAuthApp ?? false));

const schemeSupportMode = computed<string | undefined>(() => retiree.value?.finalSalaries?.schemes[0]?.supportMode);

function onViewed() {
    Monitoring.trackEvent('stepCompleted', { stepName: 'Finish' });
}
</script>

<style scoped>
    .questionText {
        margin-top: 18px;
        margin-bottom: 8px;
    }

    .warningMessage {
        color: #003d99 !important;
    }

    .disclaimer {
        font-size: 0.75rem !important;
    }

    .poweredByGuiide {
        width: 80px;
        height: auto;
    }

    .text-success {
        color: #178830 !important;
    }
</style>
